import React from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2, Eye, EyeOff, Lock, UploadCloud } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { useConfirm } from '../../../contexts/ConfirmDialogContext'

export function PhotoCard({ photo, revealed, onToggleReveal, onDelete, onClick }) {
  const { t } = useTranslation()
  const confirm = useConfirm()

  const handleDelete = async (e) => {
    e.stopPropagation()
    const ok = await confirm({
      title: t('phoenixTracker.photos.confirmDelete'),
      description: t('phoenixTracker.photos.confirmDeleteDesc'),
      confirmLabel: t('phoenixTracker.photos.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) await onDelete(photo)
  }

  const handleImageClick = (e) => {
    e.stopPropagation()
    if (!revealed) {
      onToggleReveal(photo.id)
    } else {
      onClick(photo)
    }
  }

  const handleHide = (e) => {
    e.stopPropagation()
    onToggleReveal(photo.id)
  }

  return (
    <div className="relative group rounded-lg overflow-hidden border border-border bg-card">
      <button
        onClick={handleImageClick}
        className="block w-full aspect-square overflow-hidden focus:outline-none focus:ring-2 focus:ring-app-primary-500 relative"
        aria-label={revealed ? 'View photo' : t('phoenixTracker.photos.tapToReveal')}
      >
        {photo.signedUrl ? (
          <img
            src={photo.signedUrl}
            alt={photo.caption || photo.pose}
            loading="lazy"
            className={cn(
              'w-full h-full object-cover transition-all duration-300',
              revealed ? 'group-hover:scale-105' : 'blur-2xl scale-110'
            )}
          />
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground text-xs">
            ...
          </div>
        )}

        {/* Privacy overlay — shown when not revealed */}
        {!revealed && photo.signedUrl && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 text-white pointer-events-none">
            <Eye className="w-7 h-7 mb-1" />
            <span className="text-xs font-medium">{t('phoenixTracker.photos.tapToReveal')}</span>
          </div>
        )}
      </button>

      {/* Pose badge + local-storage lock */}
      <div className="absolute top-2 start-2 flex items-center gap-1 z-10">
        <span className="px-2 py-1 rounded-md bg-black/60 text-white text-xs font-medium">
          {t(`phoenixTracker.photos.${photo.pose}`)}
        </span>
        {photo.storage === 'local' && (
          <span
            className="p-1 rounded-md bg-app-primary-500/90 text-white"
            title={t('phoenixTracker.photos.storage.localBadge')}
            aria-label={t('phoenixTracker.photos.storage.localBadge')}
          >
            <Lock className="w-3 h-3" />
          </span>
        )}
        {photo.storage === 'pending' && (
          <span
            className="p-1 rounded-md bg-blue-500/90 text-white animate-pulse"
            title={t('phoenixTracker.photos.storage.pendingBadge')}
            aria-label={t('phoenixTracker.photos.storage.pendingBadge')}
          >
            <UploadCloud className="w-3 h-3" />
          </span>
        )}
      </div>

      {/* Delete button */}
      <button
        onClick={handleDelete}
        className="absolute top-2 end-2 p-2 rounded-md transition-colors z-10 bg-black/60 text-white hover:bg-red-500"
        aria-label={t('phoenixTracker.photos.delete')}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>

      {/* Re-blur button — only when revealed */}
      {revealed && photo.signedUrl && (
        <button
          onClick={handleHide}
          className="absolute bottom-2 end-2 p-2 rounded-md transition-colors z-10 bg-black/60 text-white hover:bg-black/80"
          aria-label={t('phoenixTracker.photos.hide')}
          title={t('phoenixTracker.photos.hide')}
        >
          <EyeOff className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Caption */}
      {photo.caption && (
        <p className="px-2 py-1.5 text-xs italic text-muted-foreground truncate">
          {photo.caption}
        </p>
      )}
    </div>
  )
}
